import { Icon, Menu } from "@mindlercare/ui-web";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "components/typography";
import {
  TreatmentStatusTabs,
  type TreatmentStatusTabCounts,
  type TreatmentStatusTabValue,
} from "./TreatmentStatusTabs";
import { FilterContainer } from "./styled";
import { BookingStatus, BookingStatusCounts } from "./types";
import { bookingStatuses, treatmentStatusTranslationKeyMap } from "./utils";

type PatientsFilteringProps = {
  treatmentStatusFilter: TreatmentStatusTabValue;
  treatmentStatusCounts: TreatmentStatusTabCounts;
  onTreatmentStatusChange: (value: TreatmentStatusTabValue) => void;
  showBookingStatusFilter: boolean;
  bookingStatus: BookingStatus;
  onBookingStatusChange: (value: BookingStatus) => void;
  bookingStatusCounts: BookingStatusCounts;
};

export const PatientsFiltering = ({
  treatmentStatusFilter,
  treatmentStatusCounts,
  onTreatmentStatusChange,
  showBookingStatusFilter,
  bookingStatus,
  onBookingStatusChange,
  bookingStatusCounts,
}: PatientsFilteringProps) => {
  const { t } = useTranslation();

  const bookingStatusOptions = useMemo(
    () =>
      bookingStatuses.map(({ value, translationKey }) => {
        const count = bookingStatusCounts[value ?? "ALL"] ?? 0;

        return {
          value,
          label: `${t(translationKey)} (${count})`,
        };
      }),
    [bookingStatusCounts, t],
  );

  const activeBookingStatusOption = useMemo(() => {
    return (
      bookingStatusOptions.find((option) => option.value === bookingStatus) ??
      bookingStatusOptions[0]
    );
  }, [bookingStatus, bookingStatusOptions]);

  const statusTranslationKey = !treatmentStatusFilter
    ? "ALL"
    : treatmentStatusTranslationKeyMap[treatmentStatusFilter];
  const currentCount =
    treatmentStatusCounts[treatmentStatusFilter ?? "ALL"] ?? 0;
  const selectedTabHeading = `${t(statusTranslationKey)} ${t("PATIENTS").toLowerCase()} (${currentCount})`;

  return (
    <>
      <TreatmentStatusTabs
        value={treatmentStatusFilter}
        onChange={onTreatmentStatusChange}
        counts={treatmentStatusCounts}
      />

      <FilterContainer>
        <Typography insensitive variant="h3">
          {selectedTabHeading}
        </Typography>
        {showBookingStatusFilter && (
          <Menu>
            <Menu.Trigger
              variant="secondary"
              prefixIcon={<Icon.Filter />}
              showClear={bookingStatus !== undefined}
              onClear={() => onBookingStatusChange(undefined)}
              withExpandIcon
            >
              {activeBookingStatusOption.label}
            </Menu.Trigger>
            <Menu.Items>
              {bookingStatusOptions.map((option) => (
                <Menu.Item
                  key={option.label}
                  selected={option.value === bookingStatus}
                  onClick={() =>
                    onBookingStatusChange(option.value as BookingStatus)
                  }
                >
                  {option.label}
                </Menu.Item>
              ))}
            </Menu.Items>
          </Menu>
        )}
      </FilterContainer>
    </>
  );
};
